import {
  type AggregateOp,
  aggregatesFor,
  type FieldType,
  type GroupableKind,
  groupableKindOf,
  isCountAggregateOp,
} from '@ofmos/shared/field-types';
import { and, condition, type FilterNode } from '@ofmos/shared/filter-ast';
import {
  type AggregateRequest,
  type AggregateValue,
  bucketBounds,
  type DateGranularity,
  type GroupDimension,
  type GroupKey,
  type GroupKeyPart,
} from '@ofmos/shared/record-group';
import type { ViewColumnAggregates } from '@ofmos/shared/view';
import type { FieldMetadata, FieldValue } from '../../../data/client';
import { optionById } from '../../../fields/fieldTypeMeta';
import { DISPLAY_LOCALE, formatDate, formatDateTime, formatNumber } from '../../../fields/format';
import type { TagTone } from '../../../ui';
import type { TableColumn } from './tableModel';

/**
 * THE GROUPED TABLE'S PROJECTION OF METADATA (`docs/decisions/group-by-aggregation.md`
 * D11, D12, D18, D19, D24), as pure functions.
 *
 * Everything the grouped table has to be RIGHT about is decidable without React: which
 * fields may be a dimension, what a group's own filter is when somebody asks for its rows,
 * what a drop into it writes, and how a number renders when the answer is "nothing". Those
 * are the four places a grouped table lies to people, and every one of them is a function
 * a test can call.
 *
 * WHAT IS NOT HERE: the capability tables. Which types may be a dimension and which
 * aggregates each accepts are REGISTRY members in `field-types.ts` (D14), read through
 * `groupableKindOf` and `aggregatesFor` - a second table here is exactly the drift INV-12
 * exists to prevent.
 */

/**
 * HOW MANY GROUPS OPEN THEMSELVES (D22).
 *
 * There is no virtualization anywhere in `packages/web`, so at D20's caps a fully expanded
 * grouped table would be 200 groups x 50 rows = 10,000 real DOM rows. Rendering the rest
 * collapsed bounds the cost by what somebody has actually opened. Twenty arrived at the
 * same number from the other end and put it in its documentation as advice; advice in a
 * doc is not a bound.
 */
export const GROUPS_OPEN_BY_DEFAULT = 10;

/**
 * THE READER'S OWN ZONE, sent as an explicit IANA name on every grouped read, with no
 * server default and no fallback (D13, Arif 2026-08-16).
 *
 * The server is UTC and the person reading "payments per month" is the person whose month
 * it is. Two people in two countries therefore see two groupings of one saved view, which
 * is correct - and is the thing a stored per-view zone would get wrong the moment somebody
 * travels.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * A dimension resolved against the field list: the stored shape plus the metadata every
 * renderer needs. `null` from `resolveDimension` means the table is a flat list.
 */
export interface ResolvedDimension {
  readonly dimension: GroupDimension;
  readonly field: FieldMetadata;
  /** `'bucketed'` is a DATE or a DATETIME, which is what refuses a drop (D24). */
  readonly kind: GroupableKind;
}

/**
 * THE UI SHIPS ONE DIMENSION (D4). The query layer takes three and the wire type carries
 * an array, so the second level is the next step rather than a different feature - what
 * has to be designed before it ships is the nesting in the DOM, not the query. Anything
 * past the first entry is ignored here rather than half-rendered.
 */
export function resolveDimension(
  groupBy: readonly GroupDimension[] | null,
  fields: readonly FieldMetadata[],
): ResolvedDimension | null {
  const dimension = groupBy?.[0];
  if (dimension === undefined) return null;
  const field = fields.find((candidate) => candidate.id === dimension.fieldId);
  if (field === undefined) return null;
  const kind = groupableKindOf(field.type);
  if (kind === 'none') return null;
  return { dimension, field, kind };
}

/**
 * WHICH COLUMNS MAY BE A DIMENSION (D11, D14, and the red team's m4).
 *
 * Two filters, and the second is the one that needed writing down. `groupable !== 'none'`
 * is the registry's own answer. On top of it the picker drops SYSTEM fields, because
 * `created_at`, `updated_at` and `deleted_at` are all DATETIME and "group by deleted_at"
 * is not a menu item anybody meant to ship - it is a UI rule, not a server one, since
 * grouping by `created_at` through the API is legitimate and is the one date bucket a user
 * would actually ask for.
 *
 * That second filter cannot fire today: `isDisplayableField` drops system fields at the
 * metadata boundary, so no screen ever holds one. It is written anyway, because the
 * picker's own rule should not depend on a filter three layers away staying where it is.
 */
export function groupableColumns(columns: readonly TableColumn[]): readonly TableColumn[] {
  return columns.filter(
    (column) => !column.field.isSystem && groupableKindOf(column.field.type) !== 'none',
  );
}

/** Whether this dimension needs a granularity picker beside it (D13). */
export function isBucketedField(field: FieldMetadata): boolean {
  return groupableKindOf(field.type) === 'bucketed';
}

/**
 * THE DIMENSION A PICKER PRODUCES for one field, with the two type-dependent halves the
 * server checks (D13): a bucket carries a granularity, and a DATETIME bucket additionally
 * carries the reader's zone while a DATE carries none - a `date` column has no instant to
 * shift, and accepting a zone there would let two callers get two different bucketings of
 * the same immutable value.
 */
export function dimensionFor(
  field: FieldMetadata,
  granularity: DateGranularity = 'MONTH',
): GroupDimension {
  if (groupableKindOf(field.type) !== 'bucketed') return { fieldId: field.id };
  if (field.type === 'DATE') return { fieldId: field.id, granularity };
  return { fieldId: field.id, granularity, timeZone: browserTimeZone() };
}

/**
 * WHAT ONE GROUP'S ROWS ARE, as an ordinary filter (D19).
 *
 * This is the second half of D11's bounded-cardinality rule, and it is a hard rule rather
 * than a nicety: a dimension whose group cannot be written as a condition on the SAME
 * field may not be a dimension at all. It is what makes "show all the rows in this group"
 * an ordinary `POST /api/query` with the whole existing keyset machinery underneath it,
 * instead of a second pagination grammar.
 *
 * THE BUCKET ARM IS HALF-OPEN, and `between` is specifically refused: `temporalBetween`
 * compiles to `>=` AND `<=`, inclusive on both ends, so a record sitting exactly on the
 * next bucket's start would appear in BOTH adjacent groups' row lists while `date_trunc`
 * counted it in exactly one - a group whose rows disagree with its own count.
 */
export function groupCondition(key: GroupKey, resolved: ResolvedDimension): FilterNode {
  const part = key[0];
  const fieldId = resolved.field.id;
  if (part === undefined || part.kind === 'null') {
    // The no-value group is `isEmpty`, never `eq null` - the same condition the board's
    // trailing bucket already uses (D16).
    return condition(fieldId, 'isEmpty');
  }
  switch (part.kind) {
    case 'option':
      return condition(fieldId, 'eq', part.optionId);
    case 'boolean':
      return condition(fieldId, 'eq', part.value);
    case 'relation':
      return condition(fieldId, 'eq', part.recordId);
    case 'bucket': {
      const bounds = bucketBounds(part, resolved.dimension);
      return and(condition(fieldId, 'gte', bounds.start), condition(fieldId, 'lt', bounds.end));
    }
  }
}

/** The group's own condition ANDed onto the view's, never replacing it. */
export function groupRowsFilter(
  key: GroupKey,
  resolved: ResolvedDimension,
  viewFilter: FilterNode | undefined,
): FilterNode {
  const own = groupCondition(key, resolved);
  return viewFilter === undefined ? own : and(viewFilter, own);
}

/**
 * WHAT A DROP INTO THIS GROUP WRITES, or a refusal (D24).
 *
 * SELECT, BOOLEAN and RELATION MANY_TO_ONE write the group's value through the ordinary
 * record write, and the no-value group writes null - which is exactly what the board
 * already does and documents.
 *
 * A DATE or DATETIME BUCKET REFUSES. Dropping into "March" would have to invent a day, and
 * inventing one silently is how a due date becomes the 1st of the month for a whole
 * quarter. The refusal is structural rather than a check at the end: a bucketed dimension
 * registers no drop targets and its rows carry no drag handle, so the drop is never
 * accepted-and-ignored.
 */
export function groupDropValue(key: GroupKey): { readonly value: FieldValue } | null {
  const part = key[0];
  if (part === undefined || part.kind === 'null') return { value: null };
  switch (part.kind) {
    case 'option':
      return { value: part.optionId };
    case 'boolean':
      return { value: part.value };
    case 'relation':
      return { value: part.recordId };
    case 'bucket':
      return null;
  }
}

/** Whether records may be dragged between this dimension's groups at all (D24). */
export function acceptsGroupDrops(resolved: ResolvedDimension): boolean {
  return resolved.kind !== 'bucketed';
}

/** The one sentence a bucketed dimension owes the user in place of a drag handle. */
export const BUCKET_DROP_REFUSAL =
  'Records can’t be dragged between date groups, because a month has no single day to drop into.';

/**
 * A GROUP'S NAME, in the reader's own words.
 *
 * The server hands back stable KEYS and resolves exactly one label - a relation target's,
 * because that is the only one the browser cannot look up. Everything else is translated
 * here, on output: a SELECT label comes from metadata the browser already holds, and a
 * bucket is formatted in the browser's locale (D13's rule, and the one lesson worth
 * keeping from Twenty's cyclic buckets).
 *
 * "No value" IS THE LABEL, plainly, and never a blank cell or the word the database uses.
 */
export function groupLabel(
  key: GroupKey,
  resolved: ResolvedDimension,
  labels: readonly (string | null)[],
): string {
  const part = key[0];
  if (part === undefined || part.kind === 'null') return 'No value';
  switch (part.kind) {
    case 'option':
      return optionById(resolved.field, part.optionId)?.label ?? 'Removed Option';
    case 'boolean':
      return part.value ? 'Yes' : 'No';
    case 'relation':
      // A missing label means the caller may not read the target, or the row it points at
      // is gone (D8). Neither is an error, and neither is a uuid worth showing.
      return labels[0] ?? 'Unnamed record';
    case 'bucket':
      return bucketLabel(part);
  }
}

/** The badge tone a SELECT group header borrows from its own option, if it has one. */
export function groupTone(key: GroupKey, resolved: ResolvedDimension): TagTone | null {
  const part = key[0];
  if (part?.kind !== 'option') return null;
  return optionById(resolved.field, part.optionId)?.color ?? null;
}

/**
 * A bucket start as a period, not as a date. "August 2026" rather than "Aug 01, 2026",
 * because the group is the whole month and a start date printed alone reads as one day.
 */
function bucketLabel(part: GroupKeyPart & { readonly kind: 'bucket' }): string {
  const [year = 0, month = 1, day = 1] = part.start.split('-').map(Number);
  const at = new Date(Date.UTC(year, month - 1, day));
  const format = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(DISPLAY_LOCALE, { ...options, timeZone: 'UTC' }).format(at);
  switch (part.granularity) {
    case 'DAY':
      return formatDate(part.start);
    case 'WEEK':
      return `Week of ${formatDate(part.start)}`;
    case 'MONTH':
      return format({ month: 'long', year: 'numeric' });
    case 'QUARTER':
      return `Q${String(Math.floor((month - 1) / 3) + 1)} ${String(year)}`;
    case 'YEAR':
      return String(year);
  }
}

/** The granularity picker's own vocabulary, in the words a person uses for a period. */
export const GRANULARITY_LABELS: Readonly<Record<DateGranularity, string>> = {
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month',
  QUARTER: 'Quarter',
  YEAR: 'Year',
};

/**
 * WHAT AN OPERATION IS CALLED ON A GIVEN FIELD (D12).
 *
 * `min` and `max` on a DATE or a DATETIME are "Earliest" and "Latest": same SQL, different
 * word, and it is the right call for the reason Twenty made it - nobody says "the minimum
 * close date".
 */
export function aggregateLabel(op: AggregateOp, fieldType: FieldType | null): string {
  const temporal = fieldType === 'DATE' || fieldType === 'DATETIME';
  switch (op) {
    case 'count':
      return 'Number of records';
    case 'countValues':
      return 'Filled in';
    case 'countEmpty':
      return 'Empty';
    case 'countUnique':
      return 'Distinct values';
    case 'sum':
      return 'Sum';
    case 'avg':
      return 'Average';
    case 'min':
      return temporal ? 'Earliest' : 'Smallest';
    case 'max':
      return temporal ? 'Latest' : 'Largest';
    case 'countTrue':
      return 'Yes';
    case 'countFalse':
      return 'No';
  }
}

/** The operations a column may carry in the footer, in the registry's own order (D14). */
export function aggregateOptionsFor(field: FieldMetadata): readonly AggregateOp[] {
  return aggregatesFor(field.type);
}

/**
 * ONE GROUPED READ SERVES BOTH SETTINGS, and this is the index that keeps them apart.
 *
 * The per-group number beside a header and the per-column footer are two SETTINGS (D18)
 * and one REQUEST: the grouped statement already carries every requested aggregate for
 * every group, and the grand total is the same statement with an empty `groupBy`. So the
 * requests are assembled once, in a deterministic order, and each renderer looks its own
 * answer up by index - because `RecordGroup.aggregates` is index-aligned with the request
 * and nothing else identifies an entry.
 */
export interface GroupAggregatePlan {
  readonly requests: readonly AggregateRequest[];
  /** Where the header's number sits, or `null` when the header shows the row count. */
  readonly headerIndex: number | null;
  /** Field id to index, for the footer cells. */
  readonly footerIndex: ReadonlyMap<string, number>;
}

export function groupAggregatePlan(
  groupAggregate: AggregateRequest | null,
  columnAggregates: ViewColumnAggregates | null,
  columns: readonly TableColumn[],
): GroupAggregatePlan {
  const requests: AggregateRequest[] = [];
  const footerIndex = new Map<string, number>();

  let headerIndex: number | null = null;
  if (groupAggregate !== null) {
    headerIndex = 0;
    requests.push(groupAggregate);
  }
  // In COLUMN order rather than in the stored object's key order, so the footer's request
  // list is stable across renders and across two views that pinned the same operations.
  for (const column of columns) {
    const op = columnAggregates?.[column.field.id];
    if (op === undefined) continue;
    footerIndex.set(column.field.id, requests.length);
    requests.push({ op, fieldId: column.field.id });
  }
  return { requests, headerIndex, footerIndex };
}

/** One aggregate answer, as the lines a cell renders (D18: one line per currency code). */
export type AggregateLines = readonly string[];

/**
 * HOW A NUMBER RENDERS WHEN THE ANSWER IS "NOTHING" (D12 semantic decision 3).
 *
 * A count-family operation renders `0` and the four value operations render an em dash,
 * because there is nothing to average - and nothing is ever COALESCEd, which is the
 * difference between our empty group and Twenty's, whose server says NULL and whose client
 * says 0 for the same question.
 *
 * A null here can only mean "no rows, or no non-null values": the FORBIDDEN case cannot
 * reach a value at all, because a denied dimension or aggregate field refuses the whole
 * request (D8). That is why this must not borrow `StatTile`'s em dash casually - there it
 * means "you may not see this", and the two are different sentences.
 */
export function aggregateLines(
  value: AggregateValue | undefined,
  op: AggregateOp,
  field: FieldMetadata | null,
): AggregateLines {
  if (value === undefined) return [EM_DASH];
  if (value.kind === 'currency') {
    // One line per code, in the order the discovery statement returned them, and nothing
    // is ever summed across them: `StatRow.tsx`'s rule - "Two numbers side by side is the
    // honest rendering. They are never summed" - applied to a cell instead of to a row.
    return value.amounts.map((amount) =>
      amount.amountMicros === null
        ? `${amount.currencyCode} ${EM_DASH}`
        : formatCurrencyMicros(amount.amountMicros, amount.currencyCode),
    );
  }
  return [scalarLine(value.value, op, field)];
}

const EM_DASH = '—';

function scalarLine(
  value: number | string | null,
  op: AggregateOp,
  field: FieldMetadata | null,
): string {
  if (value === null) return isCountAggregateOp(op) ? '0' : EM_DASH;
  if (typeof value === 'string') {
    // `min`/`max` over a temporal column come back as the stored ISO string.
    return field?.type === 'DATETIME' ? formatDateTime(value) : formatDate(value);
  }
  if (isCountAggregateOp(op)) return formatNumber(value);
  return field?.type === 'CURRENCY' ? formatNumber(value / MICROS_PER_UNIT) : formatNumber(value);
}

/** Micros are exact integers; the display divides only at the very end (`fields/format`). */
const MICROS_PER_UNIT = 1_000_000;

function formatCurrencyMicros(amountMicros: number, currencyCode: string): string {
  const amount = amountMicros / MICROS_PER_UNIT;
  try {
    return new Intl.NumberFormat(DISPLAY_LOCALE, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unknown ISO code must not blank the cell, exactly as `formatCurrency` decides.
    return `${formatNumber(amount)} ${currencyCode}`;
  }
}

/**
 * The header's own number, as a sentence rather than a bare figure: "12 records" reads as
 * a count, "12" beside a group name reads as an index.
 */
export function groupCountLabel(count: number): string {
  return `${formatNumber(count)} ${count === 1 ? 'record' : 'records'}`;
}
